import { Material, Supplier, Category, AuditLog, DashboardStats } from '../types';

// Generate unique IDs
export const generateId = () => Math.random().toString(36).substr(2, 9);

// Material Top Categories
export const materialTopCategories = [
  'Woven',
  'Warp Knit',
  'Weft Knit',
  'Flat Knit Yarn',
  'Non-Woven',
  'Non-Textile',
];

// Material Sub Categories
export const materialSubCategories = [
  'Poplin', 'Canvas', 'Broadcloth', 'Chambray', 'Twill', 'Sateen', 'Oxford', 'Dobby',
  'Jacquard', 'Plain weave', 'Basket weave', 'Herringbone', 'Gabardine', 'Taffeta',
  'Serge', 'Moleskin', 'Flannel', 'Voile', 'Lawn', 'Batiste', 'Organdy', 'Muslin',
  'Calico', 'Ticking', 'Denim', 'Corduroy', 'Velvet', 'Velour', 'Fleece', 'Sherpa',
  'Faux fur', 'Suede fabric', 'Microsuede', 'Crepe', 'Chiffon', 'Tulle', 'Netting',
  'Lace', 'Sequin fabric', 'Burnout fabric', 'Raschel', 'Power net', 'Mesh warp knit'
];

// Fiber Contents
export const fiberContents = [
  'Cotton', 'Organic Cotton', 'Linen', 'Silk', 'Wool', 'Cashmere', 'Mohair',
  'Polyester', 'Recycled Polyester', 'Nylon', 'Recycled Nylon', 'Acrylic',
  'Rayon', 'Viscose', 'Modal', 'Tencel/Lyocell', 'Acetate', 'Hemp', 'Jute',
  'Bamboo', 'Soybean fiber', 'Milk fiber', 'Seacell', 'Kapok', 'Ramie',
  'Alpaca', 'Camel hair', 'Yak wool', 'Angora', 'Spandex/Lycra', 'Elastane'
];

// Branded Fiber
export const brandedFibers = [
  'BCI', 'CMIA', 'OCS', 'GOTS', 'GRS', 'OEKO-TEX', 'BLUESIGN', 'Cradle to Cradle',
  'Cotton LEADS', 'REPREVE', 'TENCEL'
];

// Material Finishes
export const materialFinishes = [
  'Peach', 'Brushed', 'Sueded', 'Airo', 'Bio polish', 'Shearing', 'Calendering',
  'Sanforizing', 'Mercerization', 'Heat setting', 'Softener', 'Water repellent',
  'Oil repellent', 'Anti-static', 'Anti-bacterial', 'UV protection', 'Wrinkle free',
  'Easy care', 'Moisture wicking', 'Quick dry', 'Thermal regulation', 'Waterproof',
  'Breathable', 'Flame retardant', 'Stiffener', 'Starch finish'
];

// Fabric Density
export const fabricDensities = [
  'Warp pick per inch',
  'Weft pick per inch',
  'Wales',
  'Course'
];

// Impression Intents
export const impressionIntents = [
  'Solid',
  'Yarn Dye',
  'AOP',
  'Fiber Dye'
];

// Material Functions
export const materialFunctions = [
  'Apparel', 'Outerwear', 'Activewear', 'Underwear', 'Swimwear', 'Workwear',
  'Uniform', 'Home textile', 'Upholstery', 'Industrial', 'Medical', 'Automotive'
];

// Countries
export const countries = [
  'China', 'China - Hong Kong', 'Vietnam', 'Bangladesh', 'India', 'Pakistan',
  'Turkey', 'Italy', 'Japan', 'South Korea', 'Thailand', 'Indonesia',
  'Myanmar', 'Cambodia', 'Sri Lanka', 'Taiwan', 'USA', 'Germany',
  'France', 'UK', 'Spain', 'Portugal', 'Egypt', 'Morocco',
  'Ethiopia', 'Kenya', 'Madagascar', 'Brazil', 'Colombia', 'Peru'
];

// Stock status options removed (no longer used)

// Status options
export const statusOptions = [
  { value: 'pending', label: 'Pending Review', color: 'yellow' },
  { value: 'approved', label: 'Approved', color: 'green' },
  { value: 'archived', label: 'Archived', color: 'gray' },
];

// Mock Suppliers
export const mockSuppliers: Supplier[] = [
  {
    id: 'sup-001',
    name: 'Zhangwei Textile Co.',
    code: 'ZW-001',
    contact: 'Zhang Wei',
    email: 'zhangwei@zhangweitextile.com',
    phone: '+86 21 8888 1234',
    address: 'No. 1234 Nanjing Road, Shanghai',
    region: 'East China',
    rating: 4.5,
    materials: ['mat-001', 'mat-002', 'mat-003'],
    createdAt: '2024-01-15',
    updatedAt: '2024-03-20',
  },
  {
    id: 'sup-002',
    name: 'Guangzhou Silk Factory',
    code: 'GSF-002',
    contact: 'Li Ming',
    email: 'liming@gzsilk.com',
    phone: '+86 20 6666 5678',
    address: 'No. 567 Silk Road, Guangzhou',
    region: 'South China',
    rating: 4.8,
    materials: ['mat-004', 'mat-005'],
    createdAt: '2024-02-10',
    updatedAt: '2024-03-18',
  },
  {
    id: 'sup-003',
    name: 'Shandong Cotton Group',
    code: 'SCG-003',
    contact: 'Wang Fang',
    email: 'wangfang@sdcotton.cn',
    phone: '+86 531 9999 8888',
    address: 'No. 888 Jinan Road, Jinan',
    region: 'North China',
    rating: 4.2,
    materials: ['mat-006', 'mat-007', 'mat-008'],
    createdAt: '2024-01-20',
    updatedAt: '2024-03-15',
  },
  {
    id: 'sup-004',
    name: 'Vietnam Textile Co.',
    code: 'VTC-004',
    contact: 'Nguyen Van A',
    email: 'nvana@vietnamtextile.vn',
    phone: '+84 24 3333 4444',
    address: '123 Textile Street, Hanoi',
    region: 'Vietnam',
    rating: 4.0,
    materials: ['mat-009', 'mat-010'],
    createdAt: '2024-02-25',
    updatedAt: '2024-03-10',
  },
  {
    id: 'sup-005',
    name: 'Bangladesh Knitwear Ltd.',
    code: 'BKL-005',
    contact: 'Rahim Uddin',
    email: 'rahim@bdknit.com.bd',
    phone: '+880 2 5555 6666',
    address: 'Industrial Area, Dhaka',
    region: 'Bangladesh',
    rating: 3.8,
    materials: ['mat-011', 'mat-012'],
    createdAt: '2024-03-01',
    updatedAt: '2024-03-08',
  },
  {
    id: 'sup-006',
    name: 'NYLtex',
    code: 'NYL-006',
    contact: '',
    email: '',
    phone: '',
    address: '',
    region: '',
    rating: 0,
    materials: [],
    createdAt: '2026-06-04',
    updatedAt: '2026-06-04',
  },
];

// Mock Materials - Updated with new field structure
export const mockMaterials: Material[] = [
  {
    id: 'mat-001',
    // Module 1: Material Specification
    materialTopCategory: 'Woven',
    materialSubCategory: 'Poplin',
    fiberContents: 'Cotton',
    brandedFiber: 'BCI',
    finishedWeight: 120,
    materialFinishes: ['Peach', 'Softener'],
    fabricDensity: 'Warp pick per inch',
    materialWidth: 58,
    cuttableWidth: 56,
    impressionIntents: 'Solid',
    comments: 'High quality poplin fabric, suitable for shirts and dresses',
    materialFunction: 'Apparel',
    sustainable: 'Yes',
    careRecommendations: 'Machine wash cold, tumble dry low',
    customers: ['BPH', 'WITT', 'Lascana'],
    
    // Module 2: Supply Chain
    supplierName: 'Zhangwei Textile Co.',
    materialCode: 'ZW-POP-001',
    oiCode: 'OI-2024-001',
    countryOfProduction: 'China',
    weavingFactory: 'Zhangwei Weaving Mill',
    dyeingFactory: 'Shanghai Dyeing Co.',
    printingFactory: '',
    finishingFactory: 'Zhangwei Finishing',
    
    // Legacy fields
    code: 'COT-2024-001',
    name: 'Premium Cotton Poplin 40s',
    materialType: 'Woven',
    supplier: 'Zhangwei Textile Co.',
    supplierId: 'sup-001',
    origin: 'China',
    weight: 120,
    width: 147,
    composition: '100% Cotton',
    color: 'White',
    yarnSpec: '40s/2 Cotton',
    price: 3.5,
    minOrder: 1000,
    leadTime: 15,

    status: 'approved',
    images: ['https://images.unsplash.com/photo-1558171813-4c088753af8f?w=400'],
    documents: [],
    remarks: 'High quality poplin fabric, suitable for shirts and dresses',
    createdAt: '2024-01-20',
    updatedAt: '2024-03-15',
    createdBy: 'Admin',
    testReports: [],
    certifications: ['BCI', 'OEKO-TEX'],
    // Module 3: Cost and Lead-time
    usdPerMCuttable: '3.5',
    rmbPerMCuttable: '25.0',
    costValidationDate: '2026-01-15',
    materialMOQQuantity: '500',
    materialMOQUnit: 'meter',
    materialMCQQuantity: '1000',
    materialMCQUnit: 'meter',
    sampleYardageDevTime: '7',
    bulkProductionTime: '15',
    // Module 4: Material Test
    testReportFiles: [],
    testStatus: 'Pass',
  },
  {
    id: 'mat-002',
    // Module 1: Material Specification
    materialTopCategory: 'Weft Knit',
    materialSubCategory: 'Jersey',
    fiberContents: 'Organic Cotton',
    brandedFiber: 'GOTS',
    finishedWeight: 180,
    materialFinishes: ['Bio polish', 'Softener'],
    fabricDensity: 'Wales',
    materialWidth: 60,
    cuttableWidth: 58,
    impressionIntents: 'Solid',
    comments: 'GOTS certified organic cotton, soft hand feel',
    materialFunction: 'Apparel',
    sustainable: 'Yes',
    careRecommendations: 'Machine wash cold, line dry',
    
    // Module 2: Supply Chain
    supplierName: 'Zhangwei Textile Co.',
    materialCode: 'ZW-JER-002',
    oiCode: 'OI-2024-002',
    countryOfProduction: 'China',
    weavingFactory: 'Zhangwei Knitting Mill',
    dyeingFactory: 'Shanghai Dyeing Co.',
    printingFactory: '',
    finishingFactory: 'Zhangwei Finishing',
    
    // Legacy fields
    code: 'COT-2024-002',
    name: 'Organic Cotton Jersey',
    materialType: 'Weft Knit',
    supplier: 'Zhangwei Textile Co.',
    supplierId: 'sup-001',
    origin: 'China',
    weight: 180,
    width: 152,
    composition: '100% Organic Cotton',
    color: 'Beige',
    yarnSpec: '30s Cotton',
    price: 5.2,
    minOrder: 500,
    leadTime: 20,

    status: 'approved',
    images: ['https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?w=400'],
    documents: [],
    remarks: 'GOTS certified organic cotton, soft hand feel',
    createdAt: '2024-02-01',
    updatedAt: '2024-03-10',
    createdBy: 'Admin',
    testReports: [],
    certifications: ['GOTS', 'OEKO-TEX'],
    // Module 3: Cost and Lead-time
    usdPerMCuttable: '5.2',
    rmbPerMCuttable: '37.0',
    costValidationDate: '2026-01-20',
    materialMOQQuantity: '500',
    materialMOQUnit: 'meter',
    materialMCQQuantity: '1000',
    materialMCQUnit: 'meter',
    sampleYardageDevTime: '10',
    bulkProductionTime: '20',
    // Module 4: Material Test
    testReportFiles: [],
    testStatus: 'Pass',
  },
  {
    id: 'mat-003',
    // Module 1: Material Specification
    materialTopCategory: 'Woven',
    materialSubCategory: 'Plain weave',
    fiberContents: 'Linen',
    brandedFiber: '',
    finishedWeight: 150,
    materialFinishes: ['Softener'],
    fabricDensity: 'Warp pick per inch',
    materialWidth: 55,
    cuttableWidth: 53,
    impressionIntents: 'Solid',
    comments: 'Breathable linen, ideal for summer garments',
    materialFunction: 'Apparel',
    sustainable: 'Yes',
    careRecommendations: 'Hand wash cold, iron while damp',
    
    // Module 2: Supply Chain
    supplierName: 'Zhangwei Textile Co.',
    materialCode: 'ZW-LIN-003',
    oiCode: 'OI-2024-003',
    countryOfProduction: 'China',
    weavingFactory: 'Zhangwei Weaving Mill',
    dyeingFactory: 'Shanghai Dyeing Co.',
    printingFactory: '',
    finishingFactory: 'Zhangwei Finishing',
    
    // Legacy fields
    code: 'LIN-2024-001',
    name: 'Pure Linen Plain weave',
    materialType: 'Woven',
    supplier: 'Zhangwei Textile Co.',
    supplierId: 'sup-001',
    origin: 'China',
    weight: 150,
    width: 140,
    composition: '100% Linen',
    color: 'Grey',
    yarnSpec: '14s/1 Linen',
    price: 6.8,
    minOrder: 300,
    leadTime: 25,

    status: 'approved',
    images: ['https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400'],
    documents: [],
    remarks: 'Breathable linen, ideal for summer garments',
    createdAt: '2024-02-10',
    updatedAt: '2024-03-12',
    createdBy: 'Admin',
    testReports: [],
    certifications: [],
    // Module 3: Cost and Lead-time
    usdPerMCuttable: '6.8',
    rmbPerMCuttable: '48.5',
    costValidationDate: '2026-02-01',
    materialMOQQuantity: '500',
    materialMOQUnit: 'meter',
    materialMCQQuantity: '1000',
    materialMCQUnit: 'meter',
    sampleYardageDevTime: '10',
    bulkProductionTime: '25',
    // Module 4: Material Test
    testReportFiles: [],
    testStatus: 'Conditional Tolerance',
  },
  {
    id: 'mat-004',
    // Module 1: Material Specification
    materialTopCategory: 'Woven',
    materialSubCategory: 'Sateen',
    fiberContents: 'Silk',
    brandedFiber: '',
    finishedWeight: 60,
    materialFinishes: ['Calendering'],
    fabricDensity: 'Warp pick per inch',
    materialWidth: 43,
    cuttableWidth: 41,
    impressionIntents: 'Solid',
    comments: 'Luxurious silk with glossy finish',
    materialFunction: 'Apparel',
    sustainable: 'No',
    careRecommendations: 'Dry clean only',
    
    // Module 2: Supply Chain
    supplierName: 'Guangzhou Silk Factory',
    materialCode: 'GSF-SLK-001',
    oiCode: 'OI-2024-004',
    countryOfProduction: 'China',
    weavingFactory: 'Guangzhou Silk Weaving',
    dyeingFactory: 'Guangzhou Dyeing',
    printingFactory: '',
    finishingFactory: 'Guangzhou Finishing',
    
    // Legacy fields
    code: 'SLK-2024-001',
    name: 'Mulberry Silk Charmeuse',
    materialType: 'Woven',
    supplier: 'Guangzhou Silk Factory',
    supplierId: 'sup-002',
    origin: 'China',
    weight: 60,
    width: 109,
    composition: '100% Mulberry Silk',
    color: 'Pink',
    yarnSpec: '20D Silk',
    price: 28.0,
    minOrder: 100,
    leadTime: 30,

    status: 'approved',
    images: ['https://images.unsplash.com/photo-1586075010923-2dd4570fb338?w=400'],
    documents: [],
    remarks: 'Luxurious silk with glossy finish',
    createdAt: '2024-02-15',
    updatedAt: '2024-03-08',
    createdBy: 'Admin',
    testReports: [],
    certifications: [],
    // Module 3: Cost and Lead-time
    usdPerMCuttable: '12.0',
    rmbPerMCuttable: '85.0',
    costValidationDate: '2026-02-10',
    materialMOQQuantity: '500',
    materialMOQUnit: 'meter',
    materialMCQQuantity: '1000',
    materialMCQUnit: 'meter',
    sampleYardageDevTime: '7',
    bulkProductionTime: '20',
    // Module 4: Material Test
    testReportFiles: [],
    testStatus: 'Pass',
  },
  {
    id: 'mat-005',
    // Module 1: Material Specification
    materialTopCategory: 'Woven',
    materialSubCategory: 'Voile',
    fiberContents: 'Silk',
    brandedFiber: '',
    finishedWeight: 45,
    materialFinishes: ['Softener'],
    fabricDensity: 'Warp pick per inch',
    materialWidth: 47,
    cuttableWidth: 45,
    impressionIntents: 'Solid',
    comments: 'Lightweight blend, perfect for blouses',
    materialFunction: 'Apparel',
    sustainable: 'No',
    careRecommendations: 'Dry clean or hand wash cold',
    
    // Module 2: Supply Chain
    supplierName: 'Guangzhou Silk Factory',
    materialCode: 'GSF-VOI-002',
    oiCode: 'OI-2024-005',
    countryOfProduction: 'China',
    weavingFactory: 'Guangzhou Silk Weaving',
    dyeingFactory: 'Guangzhou Dyeing',
    printingFactory: '',
    finishingFactory: 'Guangzhou Finishing',
    
    // Legacy fields
    code: 'SLK-2024-002',
    name: 'Silk-Cotton Blend Voile',
    materialType: 'Woven',
    supplier: 'Guangzhou Silk Factory',
    supplierId: 'sup-002',
    origin: 'China',
    weight: 45,
    width: 119,
    composition: '70% Silk, 30% Cotton',
    color: 'White',
    yarnSpec: '100D/2 Silk-Cotton',
    price: 18.5,
    minOrder: 200,
    leadTime: 25,

    status: 'approved',
    images: ['https://images.unsplash.com/photo-1558171814-64ae9eb27f66?w=400'],
    documents: [],
    remarks: 'Lightweight blend, perfect for blouses',
    createdAt: '2024-02-20',
    updatedAt: '2024-03-05',
    createdBy: 'Admin',
    testReports: [],
    certifications: [],
    // Module 3: Cost and Lead-time
    usdPerMCuttable: '11.5',
    rmbPerMCuttable: '82.0',
    costValidationDate: '2026-02-15',
    materialMOQQuantity: '500',
    materialMOQUnit: 'meter',
    materialMCQQuantity: '1000',
    materialMCQUnit: 'meter',
    sampleYardageDevTime: '7',
    bulkProductionTime: '20',
    // Module 4: Material Test
    testReportFiles: [],
    testStatus: 'Pass',
  },
  {
    id: 'mat-006',
    // Module 1: Material Specification
    materialTopCategory: 'Woven',
    materialSubCategory: 'Twill',
    fiberContents: 'Polyester',
    brandedFiber: 'REPREVE',
    finishedWeight: 100,
    materialFinishes: ['Peach', 'Water repellent'],
    fabricDensity: 'Warp pick per inch',
    materialWidth: 59,
    cuttableWidth: 57,
    impressionIntents: 'Solid',
    comments: 'Soft peach skin finish, popular for linings',
    materialFunction: 'Apparel',
    sustainable: 'Yes',
    careRecommendations: 'Machine wash cold, tumble dry low',
    customers: ['BPH', 'WITT', 'Lascana'],
    
    // Module 2: Supply Chain
    supplierName: 'Shandong Cotton Group',
    materialCode: 'SCG-POL-001',
    oiCode: 'OI-2024-006',
    countryOfProduction: 'China',
    weavingFactory: 'Shandong Weaving',
    dyeingFactory: 'Shandong Dyeing',
    printingFactory: '',
    finishingFactory: 'Shandong Finishing',
    
    // Legacy fields
    code: 'POL-2024-001',
    name: 'Polyester Microfiber Peach Skin',
    materialType: 'Woven',
    supplier: 'Shandong Cotton Group',
    supplierId: 'sup-003',
    origin: 'China',
    weight: 100,
    width: 150,
    composition: '100% Recycled Polyester',
    color: 'Navy',
    yarnSpec: '75D FDY',
    price: 2.1,
    minOrder: 2000,
    leadTime: 10,

    status: 'approved',
    images: ['https://images.unsplash.com/photo-1558171813-4c088753af8f?w=400'],
    documents: [],
    remarks: 'Soft peach skin finish, popular for linings',
    createdAt: '2024-02-25',
    updatedAt: '2024-03-01',
    createdBy: 'Admin',
    testReports: [],
    certifications: ['GRS'],
    // Module 3: Cost and Lead-time
    usdPerMCuttable: '2.8',
    rmbPerMCuttable: '20.0',
    costValidationDate: '2026-02-20',
    materialMOQQuantity: '300',
    materialMOQUnit: 'kg',
    materialMCQQuantity: '800',
    materialMCQUnit: 'kg',
    sampleYardageDevTime: '7',
    bulkProductionTime: '18',
    // Module 4: Material Test
    testReportFiles: [],
    testStatus: 'Pass',
  },
  {
    id: 'mat-007',
    // Module 1: Material Specification
    materialTopCategory: 'Woven',
    materialSubCategory: 'Twill',
    fiberContents: 'Cotton',
    brandedFiber: '',
    finishedWeight: 350,
    materialFinishes: ['Sanforizing'],
    fabricDensity: 'Warp pick per inch',
    materialWidth: 61,
    cuttableWidth: 59,
    impressionIntents: 'Yarn Dye',
    comments: 'Premium selvedge denim with elastane',
    materialFunction: 'Apparel',
    sustainable: 'No',
    careRecommendations: 'Machine wash cold, wash separately',
    
    // Module 2: Supply Chain
    supplierName: 'Shandong Cotton Group',
    materialCode: 'SCG-DEN-001',
    oiCode: 'OI-2024-007',
    countryOfProduction: 'China',
    weavingFactory: 'Shandong Denim Mill',
    dyeingFactory: 'Shandong Indigo Dyeing',
    printingFactory: '',
    finishingFactory: 'Shandong Finishing',
    
    // Legacy fields
    code: 'DEN-2024-001',
    name: '12oz Selvedge Denim',
    materialType: 'Woven',
    supplier: 'Shandong Cotton Group',
    supplierId: 'sup-003',
    origin: 'China',
    weight: 350,
    width: 155,
    composition: '98% Cotton, 2% Elastane',
    color: 'Navy',
    yarnSpec: '7s/1 Cotton',
    price: 8.5,
    minOrder: 500,
    leadTime: 20,

    status: 'pending',
    images: ['https://images.unsplash.com/photo-1542272617-08f086302542?w=400'],
    documents: [],
    remarks: 'Premium selvedge denim with elastane',
    createdAt: '2024-03-01',
    updatedAt: '2024-03-18',
    createdBy: 'Admin',
    testReports: [],
    certifications: [],
    // Module 3: Cost and Lead-time
    usdPerMCuttable: '4.2',
    rmbPerMCuttable: '30.0',
    costValidationDate: '2026-03-01',
    materialMOQQuantity: '500',
    materialMOQUnit: 'meter',
    materialMCQQuantity: '1000',
    materialMCQUnit: 'meter',
    sampleYardageDevTime: '10',
    bulkProductionTime: '22',
    // Module 4: Material Test
    testReportFiles: [],
    testStatus: 'Conditional Tolerance',
  },
  {
    id: 'mat-008',
    // Module 1: Material Specification
    materialTopCategory: 'Woven',
    materialSubCategory: 'Canvas',
    fiberContents: 'Cotton',
    brandedFiber: 'BCI',
    finishedWeight: 280,
    materialFinishes: ['Water repellent'],
    fabricDensity: 'Warp pick per inch',
    materialWidth: 60,
    cuttableWidth: 58,
    impressionIntents: 'Solid',
    comments: 'Heavy duty canvas for bags and outerwear',
    materialFunction: 'Outerwear',
    sustainable: 'Yes',
    careRecommendations: 'Machine wash cold, air dry',
    
    // Module 2: Supply Chain
    supplierName: 'Shandong Cotton Group',
    materialCode: 'SCG-CAN-002',
    oiCode: 'OI-2024-008',
    countryOfProduction: 'China',
    weavingFactory: 'Shandong Heavy Fabric Mill',
    dyeingFactory: 'Shandong Dyeing',
    printingFactory: '',
    finishingFactory: 'Shandong Finishing',
    
    // Legacy fields
    code: 'CAN-2024-001',
    name: 'Heavy Cotton Canvas',
    materialType: 'Woven',
    supplier: 'Shandong Cotton Group',
    supplierId: 'sup-003',
    origin: 'China',
    weight: 280,
    width: 152,
    composition: '100% Cotton',
    color: 'Khaki',
    yarnSpec: '10s/2 Cotton',
    price: 4.8,
    minOrder: 800,
    leadTime: 18,

    status: 'approved',
    images: ['https://images.unsplash.com/photo-1558171813-4c088753af8f?w=400'],
    documents: [],
    remarks: 'Heavy duty canvas for bags and outerwear',
    createdAt: '2024-03-05',
    updatedAt: '2024-03-15',
    createdBy: 'Admin',
    testReports: [],
    certifications: ['BCI'],
    // Module 3: Cost and Lead-time
    usdPerMCuttable: '2.5',
    rmbPerMCuttable: '18.0',
    costValidationDate: '2026-03-05',
    materialMOQQuantity: '300',
    materialMOQUnit: 'kg',
    materialMCQQuantity: '800',
    materialMCQUnit: 'kg',
    sampleYardageDevTime: '7',
    bulkProductionTime: '15',
    // Module 4: Material Test
    testReportFiles: [],
    testStatus: 'Pass',
  },
  {
    id: 'mat-009',
    // Module 1: Material Specification
    materialTopCategory: 'Weft Knit',
    materialSubCategory: 'Jersey',
    fiberContents: 'Cotton',
    brandedFiber: '',
    finishedWeight: 160,
    materialFinishes: ['Bio polish'],
    fabricDensity: 'Wales',
    materialWidth: 62,
    cuttableWidth: 60,
    impressionIntents: 'Solid',
    comments: 'Soft handfeel, good for t-shirts',
    materialFunction: 'Apparel',
    sustainable: 'No',
    careRecommendations: 'Machine wash warm, tumble dry',
    
    // Module 2: Supply Chain
    supplierName: 'Vietnam Textile Co.',
    materialCode: 'VTC-JER-001',
    oiCode: 'OI-2024-009',
    countryOfProduction: 'Vietnam',
    weavingFactory: 'Vietnam Knitting',
    dyeingFactory: 'Vietnam Dyeing',
    printingFactory: '',
    finishingFactory: 'Vietnam Finishing',
    
    // Legacy fields
    code: 'KNI-2024-001',
    name: 'Cotton Single Jersey',
    materialType: 'Weft Knit',
    supplier: 'Vietnam Textile Co.',
    supplierId: 'sup-004',
    origin: 'Vietnam',
    weight: 160,
    width: 157,
    composition: '100% Cotton',
    color: 'Black',
    yarnSpec: '32s Cotton',
    price: 2.8,
    minOrder: 1500,
    leadTime: 12,

    status: 'approved',
    images: ['https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?w=400'],
    documents: [],
    remarks: 'Soft handfeel, good for t-shirts',
    createdAt: '2024-03-10',
    updatedAt: '2024-03-20',
    createdBy: 'Admin',
    testReports: [],
    certifications: [],
    // Module 3: Cost and Lead-time
    usdPerMCuttable: '3.0',
    rmbPerMCuttable: '21.5',
    costValidationDate: '2026-03-10',
    materialMOQQuantity: '500',
    materialMOQUnit: 'meter',
    materialMCQQuantity: '1000',
    materialMCQUnit: 'meter',
    sampleYardageDevTime: '10',
    bulkProductionTime: '20',
    // Module 4: Material Test
    testReportFiles: [],
    testStatus: 'Fail',
  },
  {
    id: 'mat-010',
    // Module 1: Material Specification
    materialTopCategory: 'Warp Knit',
    materialSubCategory: 'Mesh warp knit',
    fiberContents: 'Polyester',
    brandedFiber: '',
    finishedWeight: 90,
    materialFinishes: ['Heat setting'],
    fabricDensity: 'Wales',
    materialWidth: 60,
    cuttableWidth: 58,
    impressionIntents: 'Solid',
    comments: 'Athletic mesh for sportswear',
    materialFunction: 'Activewear',
    sustainable: 'No',
    careRecommendations: 'Machine wash cold, do not iron',
    
    // Module 2: Supply Chain
    supplierName: 'Vietnam Textile Co.',
    materialCode: 'VTC-MES-002',
    oiCode: 'OI-2024-010',
    countryOfProduction: 'Vietnam',
    weavingFactory: 'Vietnam Warp Knitting',
    dyeingFactory: 'Vietnam Dyeing',
    printingFactory: '',
    finishingFactory: 'Vietnam Finishing',
    
    // Legacy fields
    code: 'MES-2024-001',
    name: 'Polyester Athletic Mesh',
    materialType: 'Warp Knit',
    supplier: 'Vietnam Textile Co.',
    supplierId: 'sup-004',
    origin: 'Vietnam',
    weight: 90,
    width: 152,
    composition: '100% Polyester',
    color: 'White',
    yarnSpec: '75D/36F',
    price: 1.9,
    minOrder: 2000,
    leadTime: 10,

    status: 'approved',
    images: ['https://images.unsplash.com/photo-1558171813-4c088753af8f?w=400'],
    documents: [],
    remarks: 'Athletic mesh for sportswear',
    createdAt: '2024-03-12',
    updatedAt: '2024-03-22',
    createdBy: 'Admin',
    testReports: [],
    certifications: [],
    // Module 3: Cost and Lead-time
    usdPerMCuttable: '4.5',
    rmbPerMCuttable: '32.0',
    costValidationDate: '2026-03-15',
    materialMOQQuantity: '500',
    materialMOQUnit: 'meter',
    materialMCQQuantity: '1000',
    materialMCQUnit: 'meter',
    sampleYardageDevTime: '10',
    bulkProductionTime: '22',
    // Module 4: Material Test
    testReportFiles: [],
    testStatus: 'Pass',
  },
  {
    id: 'mat-011',
    // Module 1: Material Specification
    materialTopCategory: 'Weft Knit',
    materialSubCategory: 'Fleece',
    fiberContents: 'Cotton',
    brandedFiber: '',
    finishedWeight: 280,
    materialFinishes: ['Brushed', 'Softener'],
    fabricDensity: 'Wales',
    materialWidth: 58,
    cuttableWidth: 56,
    impressionIntents: 'Solid',
    comments: 'Warm fleece for hoodies and sweatshirts',
    materialFunction: 'Apparel',
    sustainable: 'No',
    careRecommendations: 'Machine wash warm, tumble dry low',
    
    // Module 2: Supply Chain
    supplierName: 'Bangladesh Knitwear Ltd.',
    materialCode: 'BKL-FLE-001',
    oiCode: 'OI-2024-011',
    countryOfProduction: 'Bangladesh',
    weavingFactory: 'BD Knitting',
    dyeingFactory: 'BD Dyeing',
    printingFactory: '',
    finishingFactory: 'BD Finishing',
    
    // Legacy fields
    code: 'FLE-2024-001',
    name: 'Cotton Brushed Fleece',
    materialType: 'Weft Knit',
    supplier: 'Bangladesh Knitwear Ltd.',
    supplierId: 'sup-005',
    origin: 'Bangladesh',
    weight: 280,
    width: 147,
    composition: '80% Cotton, 20% Polyester',
    color: 'Grey',
    yarnSpec: '26s/1 Cotton',
    price: 3.2,
    minOrder: 1000,
    leadTime: 14,

    status: 'approved',
    images: ['https://images.unsplash.com/photo-1558171813-4c088753af8f?w=400'],
    documents: [],
    remarks: 'Warm fleece for hoodies and sweatshirts',
    createdAt: '2024-03-15',
    updatedAt: '2024-03-25',
    createdBy: 'Admin',
    testReports: [],
    certifications: [],
    // Module 3: Cost and Lead-time
    usdPerMCuttable: '3.2',
    rmbPerMCuttable: '23.0',
    costValidationDate: '2026-03-20',
    materialMOQQuantity: '500',
    materialMOQUnit: 'meter',
    materialMCQQuantity: '1000',
    materialMCQUnit: 'meter',
    sampleYardageDevTime: '7',
    bulkProductionTime: '14',
    // Module 4: Material Test
    testReportFiles: [],
    testStatus: 'Pass',
  },
  {
    id: 'mat-012',
    // Module 1: Material Specification
    materialTopCategory: 'Woven',
    materialSubCategory: 'Twill',
    fiberContents: 'Cotton',
    brandedFiber: '',
    finishedWeight: 240,
    materialFinishes: ['Mercerization', 'Softener'],
    fabricDensity: 'Warp pick per inch',
    materialWidth: 57,
    cuttableWidth: 55,
    impressionIntents: 'Solid',
    comments: 'Premium chino fabric for trousers',
    materialFunction: 'Apparel',
    sustainable: 'No',
    careRecommendations: 'Machine wash warm, iron medium',
    
    // Module 2: Supply Chain
    supplierName: 'Bangladesh Knitwear Ltd.',
    materialCode: 'BKL-CHI-002',
    oiCode: 'OI-2024-012',
    countryOfProduction: 'Bangladesh',
    weavingFactory: 'BD Weaving',
    dyeingFactory: 'BD Dyeing',
    printingFactory: '',
    finishingFactory: 'BD Finishing',
    
    // Legacy fields
    code: 'CHI-2024-001',
    name: 'Cotton Chino Twill',
    materialType: 'Woven',
    supplier: 'Bangladesh Knitwear Ltd.',
    supplierId: 'sup-005',
    origin: 'Bangladesh',
    weight: 240,
    width: 145,
    composition: '100% Cotton',
    color: 'Khaki',
    yarnSpec: '40s/2 Cotton',
    price: 3.8,
    minOrder: 1200,
    leadTime: 16,

    status: 'pending',
    images: ['https://images.unsplash.com/photo-1558171813-4c088753af8f?w=400'],
    documents: [],
    remarks: 'Premium chino fabric for trousers',
    createdAt: '2024-03-18',
    updatedAt: '2024-03-28',
    createdBy: 'Admin',
    testReports: [],
    certifications: [],
    // Module 3: Cost and Lead-time
    usdPerMCuttable: '3.8',
    rmbPerMCuttable: '27.0',
    costValidationDate: '2026-03-25',
    materialMOQQuantity: '500',
    materialMOQUnit: 'meter',
    materialMCQQuantity: '1000',
    materialMCQUnit: 'meter',
    sampleYardageDevTime: '7',
    bulkProductionTime: '16',
    // Module 4: Material Test
    testReportFiles: [],
    testStatus: 'Conditional Tolerance',
  },
];

// Mock Categories
export const mockCategories: Category[] = [
  {
    id: 'cat-001',
    name: 'Woven Fabrics',
    parentId: null,
    materialCount: 8,
  },
  {
    id: 'cat-002',
    name: 'Knit Fabrics',
    parentId: null,
    materialCount: 4,
  },
  {
    id: 'cat-003',
    name: 'Natural Fibers',
    parentId: null,
    materialCount: 6,
  },
  {
    id: 'cat-004',
    name: 'Synthetic Fibers',
    parentId: null,
    materialCount: 3,
  },
  {
    id: 'cat-005',
    name: 'Cotton',
    parentId: 'cat-003',
    materialCount: 4,
  },
  {
    id: 'cat-006',
    name: 'Silk',
    parentId: 'cat-003',
    materialCount: 2,
  },
  {
    id: 'cat-007',
    name: 'Linen',
    parentId: 'cat-003',
    materialCount: 1,
  },
  {
    id: 'cat-008',
    name: 'Polyester',
    parentId: 'cat-004',
    materialCount: 2,
  },
];

// Mock Audit Logs
export const mockAuditLogs: AuditLog[] = [
  {
    id: 'log-001',
    action: 'create',
    entityType: 'material',
    entityId: 'mat-001',
    entityName: 'Premium Cotton Poplin 40s',
    userId: 'user-001',
    userName: 'Admin',
    changes: {},
    timestamp: '2024-01-20T10:30:00Z',
  },
  {
    id: 'log-002',
    action: 'update',
    entityType: 'material',
    entityId: 'mat-001',
    entityName: 'Premium Cotton Poplin 40s',
    userId: 'user-001',
    userName: 'Admin',
    changes: {

    },
    timestamp: '2024-03-15T14:20:00Z',
  },
  {
    id: 'log-003',
    action: 'create',
    entityType: 'material',
    entityId: 'mat-002',
    entityName: 'Organic Cotton Jersey',
    userId: 'user-002',
    userName: 'Manager',
    changes: {},
    timestamp: '2024-02-01T09:15:00Z',
  },
  {
    id: 'log-004',
    action: 'create',
    entityType: 'supplier',
    entityId: 'sup-001',
    entityName: 'Zhangwei Textile Co.',
    userId: 'user-001',
    userName: 'Admin',
    changes: {},
    timestamp: '2024-01-15T11:00:00Z',
  },
  {
    id: 'log-005',
    action: 'update',
    entityType: 'material',
    entityId: 'mat-003',
    entityName: 'Pure Linen Plain weave',
    userId: 'user-001',
    userName: 'Admin',
    changes: {

    },
    timestamp: '2024-03-12T16:45:00Z',
  },
  {
    id: 'log-006',
    action: 'view',
    entityType: 'material',
    entityId: 'mat-004',
    entityName: 'Mulberry Silk Charmeuse',
    userId: 'user-003',
    userName: 'Viewer',
    changes: {},
    timestamp: '2024-03-20T08:30:00Z',
  },
  {
    id: 'log-007',
    action: 'create',
    entityType: 'material',
    entityId: 'mat-012',
    entityName: 'Cotton Chino Twill',
    userId: 'user-001',
    userName: 'Admin',
    changes: {},
    timestamp: '2024-03-18T13:00:00Z',
  },
  {
    id: 'log-008',
    action: 'update',
    entityType: 'material',
    entityId: 'mat-007',
    entityName: '12oz Selvedge Denim',
    userId: 'user-002',
    userName: 'Manager',
    changes: {
      status: { old: 'approved', new: 'pending' },
    },
    timestamp: '2024-03-18T15:30:00Z',
  },
];

// Dashboard Statistics
export const calculateDashboardStats = (materials: Material[]): DashboardStats => {
  const materialsByType: Record<string, number> = {};
  const materialsBySupplier: Record<string, number> = {};
  const materialsByTestStatus: Record<string, number> = {};
  const materialsByMOQ: Record<string, number> = {};

  materials.forEach(m => {
    // Count by top category (Material Specification)
    const type = m.materialTopCategory || m.materialType || 'Unknown';
    materialsByType[type] = (materialsByType[type] || 0) + 1;

    // Count by supplier
    const supplier = m.supplierName || m.supplier || 'Unknown';
    materialsBySupplier[supplier] = (materialsBySupplier[supplier] || 0) + 1;

    // Count by test status
    if (m.testStatus) {
      materialsByTestStatus[m.testStatus] = (materialsByTestStatus[m.testStatus] || 0) + 1;
    }

    // Count by MOQ unit
    if (m.materialMOQUnit) {
      materialsByMOQ[m.materialMOQUnit] = (materialsByMOQ[m.materialMOQUnit] || 0) + 1;
    }
  });

  return {
    totalMaterials: materials.length,
    pendingReview: materials.filter(m => m.status === 'pending').length,
    approvedMaterials: materials.filter(m => m.status === 'approved').length,
    recentUpdates: materials.filter(m => {
      const updatedAt = new Date(m.updatedAt);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return updatedAt >= thirtyDaysAgo;
    }).length,
    materialsByType,
    materialsBySupplier,
    materialsByTestStatus,
    materialsByMOQ,
  };
};

